import type { Material } from "@/api/schemas";

/**
 * Štítky stavu položky voči cenníku dodávateľa (KOLO 166, U8).
 *
 * - **výpredaj** (`je_dopredaj`) — dodávateľ ju už len doprodáva. Výbery
 *   ju neponúkajú (`ponukatelne` na serveri), Cenník a uložené zákazky
 *   áno. Keď dodávateľ v názve uviedol náhradu, server ju pošle
 *   v `nahrada` a štítok ju ukáže — user hneď vie, čo hľadať.
 * - **nie je v cenníku** (`nie_je_v_cenniku`) — pri poslednej aktualizácii
 *   z cenníka zmizla, ale zákazka ju používa, tak ostala (náhľad importu,
 *   KOLO 165). Výbery ju ponúkajú ďalej, cenu už import nemení.
 *
 * Vyradená položka má vlastný štítok s tlačidlom Obnoviť v tabuľke —
 * text a vysvetlenie dáva `vyradenaPopis`, aby sa vyradenie importom
 * dalo odlíšiť od ručného.
 */

const VYPREDAJ =
  "Výpredaj — dodávateľ ju už len doprodáva. Výbery ju neponúkajú, " +
  "uložené zákazky ju majú ďalej.";
const NIE_JE_V_CENNIKU =
  "Z cenníka dodávateľa pri poslednej aktualizácii zmizla, ale zákazka ju " +
  "používa — preto ostala. Cenu jej import už nemení; keď sa do cenníka " +
  "vráti, štítok zmizne.";

/** Text a vysvetlenie štítku vyradenej položky. */
export function vyradenaPopis(m: Material): { text: string; title: string } {
  if (m.vyradene_importom) {
    return {
      text: "vyradená importom",
      title:
        "Vyradila ju aktualizácia cenníka — z cenníka zmizla a žiadna " +
        "zákazka ju nepoužívala. Keď sa do cenníka vráti, import ju vráti sám.",
    };
  }
  return {
    text: "vyradená",
    title: "Vyradená — pickery ju neponúkajú, zákazky ju majú ďalej",
  };
}

type Props = {
  /** Stačia polia štítkov — výber z cenníka posiela aj súhrn položky. */
  material: Pick<Material, "je_dopredaj" | "nie_je_v_cenniku" | "nahrada">;
  /** Vysvetlenie ako text vedľa štítku (detail položky), nie len v tooltipe. */
  vysvetlenie?: boolean;
};

export function StavZnaky({ material: m, vysvetlenie = false }: Props): JSX.Element | null {
  if (!m.je_dopredaj && !m.nie_je_v_cenniku) return null;
  const nahrada = m.je_dopredaj ? m.nahrada : null;
  const vypredajTitle = nahrada
    ? `${VYPREDAJ}\nNáhrada podľa dodávateľa: ${nahrada}`
    : VYPREDAJ;

  return (
    <span
      className={
        vysvetlenie
          ? "flex flex-col gap-1.5"
          : "ml-1.5 inline-flex flex-wrap items-center gap-1 align-middle"
      }
    >
      {m.je_dopredaj ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span
            className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-medium text-amber-800"
            title={vypredajTitle}
          >
            výpredaj{nahrada ? ` → náhrada ${nahrada}` : ""}
          </span>
          {vysvetlenie ? (
            <span className="text-xs text-muted-foreground">{VYPREDAJ}</span>
          ) : null}
        </span>
      ) : null}
      {m.nie_je_v_cenniku ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span
            className="rounded-full border border-destructive/40 px-1.5 py-0 text-[10px] font-medium text-destructive"
            title={NIE_JE_V_CENNIKU}
          >
            nie je v cenníku
          </span>
          {vysvetlenie ? (
            <span className="text-xs text-muted-foreground">{NIE_JE_V_CENNIKU}</span>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
